import React, { useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Plane, Wifi, Zap, Monitor, CircleDot, Briefcase, Backpack, Loader2, ArrowRight, AlertTriangle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { formatDisplayPrice, calculateDisplayPrice, dollarsToMiles, getServiceFeeRate, withoutRetiredMilesMethod } from '@/utils';
import { getDefaultInstallmentDepositPercentForLeadDays, SLICE_PAY_HOLD_FEE_RATE, DEFERA_CAPITAL_HOLD_FEE_RATE } from '@/utils/paymentScheduleCalculator';
import { useInstallmentsCheckoutProvider } from '@/hooks/useInstallmentsCheckoutProvider';
import { getCityName } from '@/components/airportUtils';
import SegmentSeatChips from '@/components/flight/SegmentSeatChips';
import {
  getBaggageAllowanceCounts,
  formatBaggageAllowanceLabel,
  mergePurchasedBaggageIntoAllowance,
} from '@/utils/baggageAllowance';
import { resolveCarrierLogo } from '@/utils/airlineBranding';
import { fmtCurrency } from '@/utils/money';
import { isSameScheduledSlice } from '@/utils/flightIdentity';

function BaggageAllowanceIcons({ allowance, iconClassName = 'w-3 h-3', className = 'flex items-center gap-2 text-xs font-normal text-white/60' }) {
  if (!allowance) return null;

  const checkedLabel = formatBaggageAllowanceLabel(allowance.checked, allowance.checkedPrice);
  const carryOnLabel = formatBaggageAllowanceLabel(allowance.carryOn, allowance.carryOnPrice);
  const checkedTitle = allowance.checked > 0
    ? `${allowance.checked} included checked bag${allowance.checked === 1 ? '' : 's'}`
    : allowance.checkedPrice
      ? `Checked bag ${allowance.checkedPrice} (this flight)`
      : 'No included checked bags';
  const carryOnTitle = allowance.carryOn > 0
    ? `${allowance.carryOn} included carry-on bag${allowance.carryOn === 1 ? '' : 's'}`
    : allowance.carryOnPrice
      ? `Carry-on ${allowance.carryOnPrice} (this flight)`
      : 'No included carry-on';

  return (
    <div className={className}>
      <div className="flex items-center gap-1 tabular-nums" title={checkedTitle}>
        <Briefcase className={iconClassName} />
        <span>{checkedLabel}</span>
      </div>
      <div className="flex items-center gap-1 tabular-nums" title={carryOnTitle}>
        <Backpack className={iconClassName} />
        <span>{carryOnLabel}</span>
      </div>
    </div>
  );
}

/** Trip-modal flight rows use the same royal wash as the header when `gradientBottomColor` is set. */
function getOfferCardShellClass(gradientBottomColor, { selectable = true, clickable = true } = {}) {
  if (gradientBottomColor) {
    return [
      'relative backdrop-blur-md border border-brand-800/30 rounded-2xl overflow-hidden transition-all duration-200',
      'bg-gradient-to-b from-brand-900/55 via-brand-900/28 to-brand-800/35',
      'shadow-[0_4px_16px_rgba(0,0,0,0.22)]',
      clickable ? 'cursor-pointer' : 'cursor-default',
      selectable && clickable ? 'hover:border-brand-700/45' : '',
    ].filter(Boolean).join(' ');
  }
  return [
    'relative backdrop-blur-md border-[0.5px] border-white/5 rounded-2xl overflow-hidden transition-all duration-200',
    'bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)]',
    'shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)]',
    clickable ? (selectable ? 'hover:border-white/10 hover:shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.52),inset_0_-0.5px_0_0_rgba(255,255,255,0.26),inset_0_0_0_0.5px_rgba(255,255,255,0.14),0_6px_20px_rgba(0,0,0,0.26)] cursor-pointer' : 'cursor-default') : 'cursor-default',
  ].join(' ');
}

function getOfferCardRoyalOverlay(gradientBottomColor) {
  if (!gradientBottomColor) return null;
  return `linear-gradient(to top, ${gradientBottomColor}dd 0%, ${gradientBottomColor}88 40%, transparent 72%)`;
}

// Helper to format time to 12-hour format (e.g., 5:30 PM)
const formatTime12Hour = (dateTimeString) => {
    if (!dateTimeString) return '';
    try {
        const date = parseISO(dateTimeString);
        return format(date, 'h:mm a');
    } catch {
        return '';
    }
};

// Helper to parse ISO 8601 duration (e.g., "PT5H30M") or raw minutes and format it
const parseAndFormatDuration = (isoDuration) => {
    if (!isoDuration) return '';
    
    let totalMinutes = 0;
    
    // Check if it's a number (raw minutes)
    if (typeof isoDuration === 'number' || !isNaN(parseInt(isoDuration))) {
        totalMinutes = parseInt(isoDuration);
    } else {
        // Parse ISO 8601 duration string
        const matches = isoDuration.match(/PT(\d+H)?(\d+M)?/);
        if (!matches) return '';
        
        const hours = matches[1] ? parseInt(matches[1].slice(0, -1)) : 0;
        const minutes = matches[2] ? parseInt(matches[2].slice(0, -1)) : 0;
        totalMinutes = hours * 60 + minutes;
    }

    if (totalMinutes === 0) return '';
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    let result = '';
    if (hours > 0) result += `${hours} hr `;
    if (minutes > 0) result += `${minutes} min`;
    
    return result.trim();
};

// getAirlineLogo is removed as logos are now directly from API response

const getAirlineColor = (airlineName) => {
    if (!airlineName) return 'bg-gray-600'; // Added null/undefined check
    const colors = {
        'jetblue': 'bg-primary',
        'frontier': 'bg-green-600', 
        'american': 'bg-red-600',
        'delta': 'bg-primary',
        'united': 'bg-primary',
        'southwest': 'bg-orange-600',
        'alaska': 'bg-primary',
        'spirit': 'bg-yellow-500',
        'virgin': 'bg-red-600' // Virgin Atlantic
    };
    
    const key = airlineName.toLowerCase().replace(' airlines', '').replace(' ', '');
    return colors[key] || 'bg-gray-600';
};

// Helper to get full airport name
const getAirportName = (airport) => {
    if (!airport) return '';
    return airport.name || airport.city || airport.iata_code || '';
};

// Helper to clean airline name (Defera Flights style - just the name, no suffix)
const cleanAirlineName = (name) => {
    if (!name) return '';
    return name
        .replace(/\s+(Airlines|Airways|Air Lines|Airline)\s*$/i, '')
        .trim();
};

/**
 * Inventory-source label shown at the bottom of the expanded flight
 * details, above the Select-flight button. Distinguishes the fare-
 * distribution channel (SerpAPI/Google Flights vs Duffel) with a
 * Defera-branded label so power users can eyeball which source a
 * given card came from. When we don't have channel info the fallback
 * is coarse (Retail / NDC+); if Duffel ever exposes wholesale-aggregator
 * hints (Travelport / GDS) on the raw offer we can upgrade the
 * mapping (e.g. "Defera Wholesale" / "Defera Bulk") without touching
 * the card — set `source_label` on the offer server-side and it wins.
 */
function getInventorySourceLabel(offer) {
    if (!offer || typeof offer !== 'object') return null;
    // Explicit backend-provided label wins (leaves room for the
    // richer mapping without touching the card).
    if (typeof offer.source_label === 'string' && offer.source_label.trim()) {
        return offer.source_label.trim();
    }
    if (offer.duffel_source === true) return 'Defera NDC+';
    // Mondee/TripPro ('Defera Wholesale') — consolidator inventory
    // merged as an independent gap-filler behind the Google scrapers
    // and Duffel. Checked before the token fallback for the same
    // reason as SearchAPI below: Mondee offers carry `mondee:`-
    // prefixed tokens that must not read as plain "Defera Retail".
    if (offer.mondee_source === true || offer._source === 'mondee') return 'Defera Wholesale';
    // SearchAPI ('Defera Retail+') — a second Google-Flights scraper
    // that supplements SerpAPI's Google inventory. Checked BEFORE the
    // generic token fallback so a SearchAPI offer carrying a
    // Google-Flights-style departure_token doesn't get mislabeled as
    // plain "Defera Retail". Recognizes both the explicit flag and
    // the unified `_source` tag used by the merge helper.
    if (offer.searchapi_source === true || offer._source === 'searchapi') return 'Defera Retail+';
    if (offer.serpapi_source === true || offer._source === 'serpapi') return 'Defera Retail';
    // Some SerpAPI-transformed offers only carry `booking_token` /
    // `departure_token` — treat the presence of either as the SerpAPI
    // signal (guards against forgotten flag on legacy offers).
    if (offer.booking_token || offer.departure_token) return 'Defera Retail';
    return null;
}

// Helper to calculate total duration from segments
const durationToMinutes = (dur) => {
    if (dur == null) return 0;
    if (typeof dur === 'number') return dur;
    if (/^\d+$/.test(String(dur).trim())) return parseInt(dur);
    const matches = String(dur).match(/PT(\d+H)?(\d+M)?/);
    if (!matches) return 0;
    const hours = matches[1] ? parseInt(matches[1].slice(0, -1)) : 0;
    const minutes = matches[2] ? parseInt(matches[2].slice(0, -1)) : 0;
    return hours * 60 + minutes;
};

const calculateTotalDuration = (segments) => {
    if (!segments || segments.length === 0) return null;

    // Total travel time = air time of every segment + ground time of every
    // layover. Summing segment durations alone understates multi-stop trips
    // by the full layover time.
    let totalMinutes = 0;
    segments.forEach((segment, i) => {
        totalMinutes += durationToMinutes(segment.duration);
        if (i > 0) {
            // Layover before this segment: prefer the stored layover_duration
            // (admin-editable), fall back to the gap between timestamps.
            let layoverMins = durationToMinutes(segment.layover_duration);
            const prev = segments[i - 1];
            if (!layoverMins && prev?.arriving_at && segment.departing_at) {
                const gap = Math.round((new Date(segment.departing_at) - new Date(prev.arriving_at)) / 60000);
                if (gap > 0) layoverMins = gap;
            }
            totalMinutes += layoverMins;
        }
    });

    if (totalMinutes === 0) return null;
    
    // Convert back to ISO 8601 duration format
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `PT${hours}H${minutes}M`;
};

function FlightOfferCard({ offer, onFlightSelect, isReturnJourney = false, hidePrice = false, hideBaggageIcons = false, status = null, variant = 'default', paymentMethod: selectedPaymentMethod = 'installments', milesBalance = 0, reserveBalanceCents = 0, segmentSeatAssignments = null, outboundDepartureDateOverride = null, gradientBottomColor = null, reserveSwitchContext = null, changeFlightContext = null, showAllInPrices = true }) {
    // A retired Miles method prices as installments, never at the old 1¢ a Mile.
    const paymentMethod = withoutRetiredMilesMethod(selectedPaymentMethod);
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);
    const isSelectable = !!onFlightSelect;
    const isLarge = variant === 'large';
    const cardRef = React.useRef(null);
    const installmentsCheckoutProvider = useInstallmentsCheckoutProvider();

    // Handle both simplified and original Duffel structures
    let slice, firstSegment, lastSegment, totalDuration, stops, marketingAirline;
    
    if (offer.slices && offer.slices.length > 0) {
        // Original Duffel structure or Defera Flights structure
        slice = (isReturnJourney && offer.slices.length > 1) ? offer.slices[1] : offer.slices[0];
        
        if (!slice) {
            return null;
        }
        
        firstSegment = slice.segments[0];
        lastSegment = slice.segments[slice.segments.length - 1];
        // Get duration from slice or calculate from segments
        totalDuration = parseAndFormatDuration(slice.duration || calculateTotalDuration(slice.segments));
        stops = slice.segments.length - 1;
        // Get airline from marketing carrier (Defera Flights) or owner (Duffel)
        marketingAirline = firstSegment.marketing_carrier?.name || offer.owner?.name;
    } else {
        // Simplified structure from backend
        if (!offer.airline) {
            return null;
        }
        
        // Create mock structure for compatibility
        totalDuration = parseAndFormatDuration(offer.duration);
        stops = offer.stops || 0;
        marketingAirline = offer.airline;
        
        // Mock segment structure
        firstSegment = {
            departing_at: offer.departureTime,
            arriving_at: offer.arrivalTime,
            origin: { iata_code: offer.origin, name: offer.origin },
            destination: { iata_code: offer.destination, name: offer.destination },
            marketing_carrier: { 
                name: offer.airline, 
                flight_number: offer.flightNumber,
                iata_code: offer.airline.substring(0, 2)
            },
            operating_carrier: { name: offer.airline },
            aircraft: { name: offer.aircraft || 'Unknown Aircraft' }
        };
        lastSegment = firstSegment;
        
        // Create mock slice with segments array
        slice = {
            duration: offer.duration,
            segments: [firstSegment],
            origin: { iata_code: offer.origin, name: offer.origin },
            destination: { iata_code: offer.destination, name: offer.destination }
        };
    }
    
    // Clean airline name (Defera Flights style)
    const displayAirlineName = cleanAirlineName(marketingAirline || '');
    const operatingCarrier = firstSegment.operating_carrier?.name;
        
    // Extract marketing carrier logo from API response
    let airlineLogo = null;
    
    if (offer.slices && offer.slices[0]) {
        // Check for logo in marketing carrier first (Defera Flights & Duffel).
        // resolveCarrierLogo is name-first: admin airline renames keep the
        // stored logo_symbol_url pointing at the OLD carrier, so the name
        // decides which mark renders. Resolve from the DISPLAYED slice first:
        // the return card labels itself with ITS carrier (marketingAirline
        // above), so a return leg on a different airline must show its own
        // mark, not the outbound's.
        airlineLogo = resolveCarrierLogo(firstSegment?.marketing_carrier);
        if (!airlineLogo && offer.slices[0].segments && offer.slices[0].segments[0]) {
            airlineLogo = resolveCarrierLogo(offer.slices[0].segments[0].marketing_carrier);
        }

        // Fallback to owner logo if available (Duffel structure)
        if (!airlineLogo) {
            airlineLogo = resolveCarrierLogo(offer.owner);
        }
    } else if (offer.duffelOffer) {
        // Simplified structure - extract logo from the original duffelOffer
        const duffelOffer = offer.duffelOffer;

        // Check if there's a marketing carrier logo in the slices
        if (duffelOffer.slices && duffelOffer.slices[0] && duffelOffer.slices[0].segments) {
            airlineLogo = resolveCarrierLogo(duffelOffer.slices[0].segments[0].marketing_carrier);
        }

        // Fallback to owner logo if available
        if (!airlineLogo && duffelOffer.owner) {
            airlineLogo = resolveCarrierLogo(duffelOffer.owner);
        }
    }

    const baseBaggageAllowance = getBaggageAllowanceCounts(offer, { isReturnLeg: isReturnJourney });
    const baggageAllowance = mergePurchasedBaggageIntoAllowance(
      baseBaggageAllowance,
      offer.flightData || offer.flight_data,
    );

    const departureTime = formatTime12Hour(firstSegment.departing_at);
    const arrivalTime = formatTime12Hour(lastSegment.arriving_at);

    // Google-style "+1" superscript on the arrival time when the slice
    // lands on a later calendar day than it departs. Compares the local
    // airport date parts as-is — timezone math would double-count what
    // the timestamps already encode.
    let arrivalDayOffset = 0;
    {
        const depDate = String(firstSegment.departing_at || '').slice(0, 10);
        const arrDate = String(lastSegment.arriving_at || '').slice(0, 10);
        if (depDate.length === 10 && arrDate.length === 10) {
            const diff = Math.round((new Date(`${arrDate}T00:00:00Z`) - new Date(`${depDate}T00:00:00Z`)) / 86400000);
            if (Number.isFinite(diff) && diff > 0) arrivalDayOffset = diff;
        }
    }

    // Layover duration for single-stop itineraries — shown next to the
    // stop info. Prefer the API's layover_duration; fall back to the gap
    // between segments.
    let layoverDuration = '';
    if (stops === 1 && slice.segments?.length === 2) {
        const [a, b] = slice.segments;
        let dur = b.layover_duration;
        if (!dur && a.arriving_at && b.departing_at) {
            const mins = Math.round((new Date(b.departing_at) - new Date(a.arriving_at)) / 60000);
            if (mins > 0) dur = mins;
        }
        layoverDuration = parseAndFormatDuration(dur);
    }

    // Longest layover across the displayed slice, for the Google-style
    // long-layover warning. 4h+ is roughly where Google Flights starts
    // flagging itineraries (and hiding them behind "show more"); since we
    // now surface Google's hidden inventory, we show the same signal
    // instead of hiding the flight.
    const LONG_LAYOVER_MINS = 240;
    let longestLayoverMins = 0;
    let longestLayoverAirport = '';
    if (Array.isArray(slice?.segments) && slice.segments.length > 1) {
        for (let i = 1; i < slice.segments.length; i++) {
            const prev = slice.segments[i - 1];
            const seg = slice.segments[i];
            let mins = durationToMinutes(seg.layover_duration);
            if (!mins && prev?.arriving_at && seg?.departing_at) {
                const gap = Math.round((new Date(seg.departing_at) - new Date(prev.arriving_at)) / 60000);
                if (gap > 0) mins = gap;
            }
            if (mins > longestLayoverMins) {
                longestLayoverMins = mins;
                longestLayoverAirport = prev?.destination?.iata_code || '';
            }
        }
    }
    const hasLongLayover = longestLayoverMins >= LONG_LAYOVER_MINS;
    const longLayoverLabel = hasLongLayover
        ? `Long layover — ${parseAndFormatDuration(longestLayoverMins)}${longestLayoverAirport ? ` in ${longestLayoverAirport}` : ''}`
        : '';

    const handleCardClick = () => {
        // Only allow expansion for selectable cards
        if (isSelectable) {
            setIsExpanded(!isExpanded);
            
            // Scroll card to center of viewport
            if (cardRef.current) {
                setTimeout(() => {
                    const cardElement = cardRef.current;
                    // The card can unmount before this fires (results re-render).
                    if (!cardElement) return;
                    const cardRect = cardElement.getBoundingClientRect();
                    const windowHeight = window.innerHeight;
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    
                    // Calculate position to center the card
                    const cardCenter = cardRect.top + scrollTop + (cardRect.height / 2);
                    const viewportCenter = windowHeight / 2;
                    const scrollTo = cardCenter - viewportCenter;
                    
                    // Smooth scroll to center
                    window.scrollTo({
                        top: scrollTo,
                        behavior: 'smooth'
                    });
                }, 50); // Small delay to ensure expansion starts
            }
        }
    };

    const handleSelectFlight = (e) => {
        e.stopPropagation(); // Prevent card from collapsing
        if (onFlightSelect) {
            onFlightSelect(offer); // Pass the entire offer object up
        }
    };

    const roundedTotalAmount = Math.round(parseFloat(offer.price || offer.total_amount));
    const offerBaseCents = Math.round(parseFloat(offer.price || offer.total_amount) * 100);

    // Reserve Switch: compare to reserved trip
    let reserveSwitchBadge = null;
    let reserveSwitchSelected = false;
    if (reserveSwitchContext && reserveSwitchContext.reserveSwitchTrip) {
      const trip = reserveSwitchContext.reserveSwitchTrip;
      const reservedSlices = trip.flight_data?.slices || [];
      const reservedBaseCents = reserveSwitchContext.reservedBaseFareCents || 0;
      if (isReturnJourney && offer.slices?.length >= 2 && reservedSlices.length >= 2) {
        reserveSwitchSelected = isSameScheduledSlice(offer.slices[0], reservedSlices[0]) && isSameScheduledSlice(offer.slices[1], reservedSlices[1]);
      } else if (!isReturnJourney && offer.slices?.[0] && reservedSlices[0]) {
        reserveSwitchSelected = isSameScheduledSlice(offer.slices[0], reservedSlices[0]);
      }
      if (reserveSwitchSelected) {
        reserveSwitchBadge = { type: 'selected', label: 'Selected' };
      } else if (reservedBaseCents > 0) {
        const diff = reservedBaseCents - offerBaseCents;
        if (diff >= 1000) reserveSwitchBadge = { type: 'cheaper', label: 'Cheaper' };
        else if (diff <= -1000) reserveSwitchBadge = { type: 'costs_more', label: 'Costs more' };
      }
    }

    // Change Flight: new base fare - original base fare. A flight the customer
    // is booked on is tagged "Current flight"; picking it is disabled only when
    // that would change nothing at all.
    let changeFlightDiff = null;
    let changeFlightCurrent = false; // this card is a flight the customer holds
    let changeFlightKeepable = false; // ...and picking it is still a real change
    if (changeFlightContext && changeFlightContext.originalBaseCents > 0) {
      const originalBaseCents = changeFlightContext.originalBaseCents;
      const trip = changeFlightContext.changeFlightTrip || [];
      const originalSlices = [];
      trip.forEach((b) => {
        const s = b?.flightData?.slices || b?.flight_data?.slices || [];
        s.forEach((sl) => originalSlices.push(sl));
      });
      let newBaseCents;
      if (isReturnJourney && changeFlightContext.selectedOutboundFlight) {
        const offerPrice = parseFloat(offer.price || offer.total_amount || 0);
        if (offerPrice > 0) {
          newBaseCents = Math.round(offerPrice * 100);
        } else {
          const outboundBase = Math.round(parseFloat(changeFlightContext.selectedOutboundFlight.price || changeFlightContext.selectedOutboundFlight.total_amount || 0) * 100);
          const returnBase = Math.round(offerPrice * 100);
          newBaseCents = outboundBase + returnBase;
        }
      } else {
        newBaseCents = Math.round(parseFloat(offer.price || offer.total_amount || 0) * 100);
      }
      // Booked-flight identity is carrier + flight number + route + date (see
      // utils/flightIdentity) — never the exact minute, which airlines retime.
      let isBookedFlight = false;
      let isNoOpChange = false;
      if (isReturnJourney) {
        // Return-selection step: the card's slices[0] IS the return leg
        // (SerpAPI's search-with-token response and Duffel's return_only
        // projection both ship 1-slice offers).
        const selectedOutboundSlice = changeFlightContext.selectedOutboundFlight?.slices?.[0];
        const outboundKept = !!(selectedOutboundSlice && originalSlices[0]
          && isSameScheduledSlice(selectedOutboundSlice, originalSlices[0]));
        const returnLeg = offer.slices?.length > 1 ? offer.slices[offer.slices.length - 1] : offer.slices?.[0];
        isBookedFlight = !!(returnLeg && originalSlices[1] && isSameScheduledSlice(returnLeg, originalSlices[1]));
        // Keep departure + keep return changes nothing. With a DIFFERENT
        // outbound, keeping the booked return is a real change: still tagged
        // Current, but selectable and priced like any other return.
        isNoOpChange = isBookedFlight && outboundKept;
      } else {
        const outboundBooked = !!(offer.slices?.[0] && originalSlices[0] && isSameScheduledSlice(offer.slices[0], originalSlices[0]));
        const returnBooked = originalSlices.length < 2 || !(offer.slices?.[1] && originalSlices[1]) || isSameScheduledSlice(offer.slices[1], originalSlices[1]);
        isBookedFlight = outboundBooked && returnBooked;
        // Round trip, outbound step: keeping the booked departure moves on to
        // the return options, so it stays selectable. One-way: no change.
        isNoOpChange = isBookedFlight && originalSlices.length < 2;
      }
      changeFlightCurrent = isBookedFlight;
      changeFlightKeepable = isBookedFlight && !isNoOpChange;
      const diffCents = newBaseCents - originalBaseCents;
      if (isNoOpChange) {
        changeFlightDiff = { cents: 0, label: 'Current', isCheaper: false, sublabel: '$0 difference', isCurrent: true };
      } else if (isBookedFlight && !isReturnJourney) {
        // The final price depends on the return picked next.
        changeFlightDiff = { cents: 0, label: 'Current', isCheaper: false, sublabel: 'keep departure', isCurrent: true };
      } else if (diffCents > 50) {
        // Above basis (Actual Cost) → bill the difference upfront. Markup (11%
        // service fee on the difference) MUST match the change-flight edge
        // function + FlightOptions + ChangeFlightReview so this badge equals the
        // amount actually charged.
        const chargeCents = Math.round(diffCents * 1.11);
        changeFlightDiff = {
          cents: chargeCents,
          label: `+$${fmtCurrency(chargeCents)}`,
          isCheaper: false,
          sublabel: 'difference'
        };
      } else {
        // At or below basis → free change. Flight credits are never shown to the
        // customer here; the admin presents any credit manually at booking time.
        changeFlightDiff = { cents: 0, label: 'No charge', isCheaper: false, sublabel: 'included' };
      }
    }
    // Beside the airline name in both layouts, so the customer can spot the
    // flight they're on without expanding the card.
    const currentFlightTag = changeFlightCurrent ? (
        <Badge className="text-xs h-5 px-1.5 py-0 shrink-0 bg-sky-400/10 text-sky-300 border-sky-400/20">Current flight</Badge>
    ) : null;

    // Calculate display price based on payment method (installments/miles/miles_installments)
    const priceBreakdown = calculateDisplayPrice(roundedTotalAmount, paymentMethod, milesBalance);
    const formattedPrice = formatDisplayPrice(roundedTotalAmount, paymentMethod, milesBalance, { compact: true });
    
    // For miles_installments in search results, show just the miles portion
    const milesOnlyDisplay = priceBreakdown.milesUsed 
        ? `+ ${priceBreakdown.milesUsed.toLocaleString()} miles`
        : null;
    
    // Deposit = % of (base fare + service fee) or (cash portion + service fee) — not total with hold
    // Defera Capital lead-time defaults (effective 2026-06-16): 5% at 90+ days, 20% at 60–89,
    // 40% at 30–59, 60% under 30 (raised from 50, 2026-08-11). Slice Pay: 20% at 60+ days, 30% under 60.
    //
    // Lead-time is measured from the TRIP START (outbound departure), not
    // from the individual leg the card is showing. For a round-trip:
    //
    //   Outbound card → offer.slices[0] IS the outbound → correct floor
    //   Return card   → offer.slices[0] is the RETURN leg (SerpAPI's
    //                   /search-with-token response and Duffel's
    //                   return_only projection both do this), so reading
    //                   slices[0].segments[0].departing_at gives the return
    //                   date, which for a typical roundtrip is 2-10 days
    //                   later than the outbound and can cross the 90-day
    //                   tier boundary (screenshot bug: outbound "$30 today"
    //                   at 20% tier, return "$8 today" at 5% tier — same
    //                   trip, same base fare).
    //
    // `outboundDepartureDateOverride` is a per-card override the parent
    // hands in on the return-selection view, populated with the ACTUAL
    // trip-start date. Prefer it when present so both cards on the same
    // trip agree on the deposit tier.
    let upfrontAmount = null;
    let leadDays = NaN;
    try {
      const depAt = outboundDepartureDateOverride
        || offer?.slices?.[0]?.segments?.[0]?.departing_at;
      if (depAt) {
        const dep = new Date(depAt);
        const today = new Date();
        dep.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        leadDays = Math.floor((dep.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      }
    } catch {
      /* keep NaN → 20% default */
    }
    const defaultDepositPct = getDefaultInstallmentDepositPercentForLeadDays(leadDays, {
      deferaCapital: installmentsCheckoutProvider === 'defera_capital',
    });
    const seatTotal = 0; // No seat selection at search stage

    if (!hidePrice && paymentMethod === 'installments') {
        try {
            const baseFare = parseFloat(offer.price || offer.total_amount);
            const serviceFeeRate = getServiceFeeRate(baseFare, installmentsCheckoutProvider === 'defera_capital');
            const serviceFee = baseFare * serviceFeeRate;
            const totalBeforeHoldFee = baseFare + serviceFee + seatTotal;
            upfrontAmount = Math.round((totalBeforeHoldFee * (defaultDepositPct / 100)) * 100) / 100;
        } catch (error) {
            console.error('Error calculating upfront payment:', error);
        }
    }

    if (!hidePrice && paymentMethod === 'miles_installments' && priceBreakdown.remainingCash && priceBreakdown.remainingCash > 0) {
        try {
            const cashPortion = priceBreakdown.remainingCash;
            const serviceFeeRate = getServiceFeeRate(cashPortion, installmentsCheckoutProvider === 'defera_capital');
            const serviceFee = cashPortion * serviceFeeRate;
            const totalBeforeHoldFee = cashPortion + serviceFee + seatTotal;
            upfrontAmount = Math.round(totalBeforeHoldFee * (defaultDepositPct / 100));
        } catch (error) {
            console.error('Error calculating upfront payment for miles+installments remainder:', error);
        }
    }

    // Reserve: show +$X needed beyond balance, or $0+ if covered
    let reserveExtra = null;
    if (!hidePrice && paymentMethod === 'reserve') {
        const baseFare = parseFloat(offer.price || offer.total_amount);
        const reserveServiceFee = baseFare * 0.11;
        const totalNeededCents = Math.round((baseFare + reserveServiceFee) * 100);
        const shortfallCents = Math.max(0, totalNeededCents - reserveBalanceCents);
        reserveExtra = shortfallCents;
    }

    // A card may only call a number "total" if it's what checkout will bill
    // (Review's grandTotalIncludingSeats math). The raw airline fare used to
    // wear the "total" label, understating the billed amount by 24-50%.
    // Financed paths: (fare + service fee) + 15% hold; Defera Zero: 11%
    // service + 15% hold; Reserve: fare + 11% service (no hold). Ceil so the
    // card never understates checkout by rounding.
    const cardHoldRate = installmentsCheckoutProvider === 'defera_capital'
        ? DEFERA_CAPITAL_HOLD_FEE_RATE
        : SLICE_PAY_HOLD_FEE_RATE;
    const cardBaseFare = parseFloat(offer.price || offer.total_amount);
    const formatCardTotal = (n) => `$${Math.ceil(n).toLocaleString('en-US')}`;
    const reserveTotalLabel = Number.isFinite(cardBaseFare)
        ? `${formatCardTotal(cardBaseFare * 1.11)} total`
        : `${formattedPrice} fare`;
    const zeroTotalLabel = Number.isFinite(cardBaseFare)
        ? `${formatCardTotal(cardBaseFare * 1.11 * (1 + cardHoldRate))} total`
        : `${formattedPrice} fare`;
    let installmentsTotalLabel = (paymentMethod === 'installments' && Number.isFinite(cardBaseFare))
        ? `${formatCardTotal((cardBaseFare + cardBaseFare * getServiceFeeRate(cardBaseFare, installmentsCheckoutProvider === 'defera_capital')) * (1 + cardHoldRate))} total`
        // miles_installments spends miles + cash — a dollars-only figure
        // can't honestly be called the "total", so label the fare as such.
        : `${formattedPrice} fare`;
    let zeroTotalLabelDisplay = zeroTotalLabel;
    // Shopping preference (useAllInPrices, default ON): when the customer
    // toggles all-in display OFF, cards show the raw base fare — always
    // labeled "fare" so the smaller number never masquerades as a total.
    // Reserve stays on its true debit total regardless (it's not a shopping
    // comparison surface), and checkout never toggles.
    if (!showAllInPrices) {
        installmentsTotalLabel = `${formattedPrice} fare`;
        zeroTotalLabelDisplay = `${formattedPrice} fare`;
    }

    const getStatusStyles = (status) => {
        switch (status) {
            case 'booked':
            case 'confirmed':
                return {
                    label: 'Confirmed',
                    className: 'bg-green-500/10 text-green-400 border-green-500/20'
                };
            case 'pending':
                 return {
                    label: 'Pending',
                    className: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                };
            case 'in_cart': // Changed from 'abandoned' to 'in_cart'
                return {
                    label: 'In Cart', // Changed from 'Abandoned Cart' to 'In Cart'
                    className: 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                };
            case 'completed':
                 return {
                    label: 'Completed',
                    className: 'bg-sky-400/10 text-sky-300 border-sky-400/20'
                };
            case 'cancelled':
                return {
                    label: 'Cancelled',
                    className: 'bg-red-500/10 text-red-400 border-red-500/20'
                };
            case 'defaulted':
                return {
                    label: 'Defaulted',
                    className: 'bg-orange-600/20 text-orange-400 border-orange-600/30'
                };
            default:
                return null;
        }
    };

    const statusInfo = getStatusStyles(status);

    // Determine desktop grid columns based on hidePrice and status presence
    const desktopGridCols = 'grid-cols-12';
    
    // Extract date range from offer data for large variant (first dep → last arr across all slices)
    let departureDate = '';
    if (offer.slices?.length > 0) {
        const firstDep = offer.slices[0]?.segments?.[0]?.departing_at;
        const lastSlice = offer.slices[offer.slices.length - 1];
        const lastSeg = lastSlice?.segments?.[lastSlice.segments?.length - 1];
        const lastArr = lastSeg?.arriving_at;
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const fmt = (dt) => {
            if (!dt) return null;
            const datePart = (dt.includes('T') ? dt.split('T')[0] : dt.split(' ')[0]) || '';
            const parts = datePart.split('-');
            if (parts.length < 3) return null;
            return `${monthNames[parseInt(parts[1]) - 1]} ${parseInt(parts[2])}`;
        };
        const start = fmt(firstDep);
        const end = fmt(lastArr);
        departureDate = (start && end && start !== end) ? `${start} – ${end}` : (start || '');
    }
    const bookingReference = offer.bookingReference || offer.booking_reference || '';

    // Large variant - simplified card for upcoming flights
    if (isLarge) {
        const handleClick = () => {
            const reference = offer.bookingReference || offer.booking_reference || offer.id;
            navigate(`/trips/${reference}`);
        };

        return (
            <div
                onClick={handleClick}
                className={getOfferCardShellClass(gradientBottomColor, { selectable: true, clickable: true })}
            >
                {gradientBottomColor && (
                    <div
                        className="absolute inset-0 pointer-events-none rounded-2xl"
                        style={{ background: getOfferCardRoyalOverlay(gradientBottomColor) }}
                    />
                )}
                <div className="relative z-10">
                {/* Mobile Layout */}
                <div className="block md:hidden p-4">
                    <div className="flex items-center gap-3">
                        {/* Airline Logo */}
                        <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0">
                            <img 
                                src={airlineLogo} 
                                alt={`${marketingAirline} logo`} 
                                className="w-8 h-8 object-contain"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = `<span class="text-white font-bold">${marketingAirline?.charAt(0) || '?'}</span>`;
                                }}
                            />
                        </div>
                        
                        {/* Route */}
                        <div className="flex-1 min-w-0">
                            <div className="text-base font-normal text-white truncate">
                                {getCityName(firstSegment.origin?.iata_code || firstSegment.origin)} → {getCityName(lastSegment.destination?.iata_code || lastSegment.destination)}
                            </div>
                            <div className="text-xs font-normal text-white/60">
                                {departureDate} • {bookingReference}
                            </div>
                        </div>

                        {/* Status Badge on the right */}
                        {statusInfo && (
                            <Badge className={`text-xs px-2 py-0.5 ${statusInfo.className} whitespace-nowrap`}>
                                {statusInfo.label}
                            </Badge>
                        )}
                        
                        {!hidePrice && (
                            <div key={showAllInPrices ? 'allin' : 'fare'} className="text-right whitespace-nowrap transform-gpu">
                                {changeFlightDiff ? (
                                    <>
                                        <div className={`text-sm font-medium ${changeFlightDiff.cents === 0 ? 'text-white' : changeFlightDiff.isCheaper ? 'text-emerald-400' : 'text-amber-400'}`}>
                                            {changeFlightDiff.label}
                                        </div>
                                        {changeFlightDiff.sublabel ? <div className="text-xs font-normal text-white/60">{changeFlightDiff.sublabel}</div> : null}
                                    </>
                                ) : reserveSwitchBadge ? (
                                    <>
                                        <div className={`text-sm font-medium ${
                                            reserveSwitchBadge.type === 'cheaper' ? 'text-emerald-400' :
                                            reserveSwitchBadge.type === 'costs_more' ? 'text-amber-400' : 'text-white/80'
                                        }`}>
                                            {reserveSwitchBadge.label}
                                        </div>
                                        <div className="text-xs font-normal text-white/60">{reserveTotalLabel}</div>
                                    </>
                                ) : paymentMethod === 'reserve' ? (
                                    reserveExtra === 0 ? (
                                        <div className="text-sm font-medium text-emerald-400">$0+</div>
                                    ) : (
                                        <>
                                            <div className="text-sm font-medium text-white">+${Math.ceil(reserveExtra / 100).toLocaleString()}</div>
                                            <div className="text-xs font-normal text-white/60">{reserveTotalLabel}</div>
                                        </>
                                    )
                                ) : upfrontAmount && paymentMethod !== 'miles' ? (
                                    <>
                                        <div className="text-sm font-medium text-white">
                                            ${upfrontAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })} today
                                        </div>
                                        <div className="text-xs font-normal text-white/60">
                                            {installmentsTotalLabel}
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-lg font-light text-white">
                                        {formattedPrice}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:block p-6">
                    <div className="flex items-center justify-between gap-6">
                        {/* Left: Logo + Route Info */}
                        <div className="flex items-center gap-4 flex-1">
                            {/* Airline Logo */}
                            <div className="w-12 h-12 rounded flex items-center justify-center flex-shrink-0">
                                <img 
                                    src={airlineLogo} 
                                    alt={`${marketingAirline} logo`} 
                                    className="w-10 h-10 object-contain"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = `<span class="text-white font-bold text-lg">${marketingAirline?.charAt(0) || '?'}</span>`;
                                    }}
                                />
                            </div>
                            
                            {/* Route and Details */}
                            <div className="flex-1">
                                <div className="text-lg font-light text-white mb-1">
                                    {getCityName(firstSegment.origin?.iata_code || firstSegment.origin)} → {getCityName(lastSegment.destination?.iata_code || lastSegment.destination)}
                                </div>
                                <div className="text-sm font-normal text-white/60">
                                    {departureDate} • {bookingReference}
                                </div>
                            </div>
                        </div>

                        {/* Right: Status Badge + Price + Arrow */}
                        <div className="flex items-center gap-4">
                            {statusInfo && (
                                <Badge className={`text-sm px-3 py-1 ${statusInfo.className}`}>
                                    {statusInfo.label}
                                </Badge>
                            )}
                            {!hidePrice && (
                                <div key={showAllInPrices ? 'allin' : 'fare'} className="text-right transform-gpu">
                                    {reserveSwitchBadge ? (
                                        <>
                                            <div className={`text-base font-medium ${
                                                reserveSwitchBadge.type === 'cheaper' ? 'text-emerald-400' :
                                                reserveSwitchBadge.type === 'costs_more' ? 'text-amber-400' : 'text-white/80'
                                            }`}>
                                                {reserveSwitchBadge.label}
                                            </div>
                                            <div className="text-xs font-normal text-white/60">{reserveTotalLabel}</div>
                                        </>
                                    ) : paymentMethod === 'reserve' ? (
                                        reserveExtra === 0 ? (
                                            <div className="text-base font-medium text-emerald-400">$0+</div>
                                        ) : (
                                            <>
                                                <div className="text-base font-medium text-white">+${Math.ceil(reserveExtra / 100).toLocaleString()}</div>
                                                <div className="text-xs font-normal text-white/60">{reserveTotalLabel}</div>
                                            </>
                                        )
                                    ) : upfrontAmount && paymentMethod !== 'miles' ? (
                                        <>
                                            <div className="text-base font-medium text-white">
                                                ${upfrontAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })} today
                                            </div>
                                            <div className="text-xs font-normal text-white/60">
                                                {installmentsTotalLabel}
                                            </div>
                                        </>
                                    ) : paymentMethod === 'defera_zero' ? (
                                        <>
                                            <div className="text-base font-medium text-white">{formattedPrice}</div>
                                            <div className="text-xs font-normal text-white/60">
                                                {zeroTotalLabelDisplay}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-xl font-light text-white">
                                            {formattedPrice}
                                        </div>
                                    )}
                                </div>
                            )}
                            <ChevronDown className="w-5 h-5 text-white/40" />
                        </div>
                    </div>
                </div>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={cardRef}
            // Let the results page find this card again after the list
            // re-renders, to hold the customer's place (see
            // holdScrollPosition in GoogleFlightsSearch.jsx).
            data-offer-id={offer?.id}
            data-expanded={isExpanded || undefined}
            className={getOfferCardShellClass(gradientBottomColor, { selectable: isSelectable, clickable: true })}
            onClick={handleCardClick}
            style={{ opacity: 1 }}
        >
            {gradientBottomColor && (
                <div
                    className="absolute inset-0 pointer-events-none rounded-2xl"
                    style={{ background: getOfferCardRoyalOverlay(gradientBottomColor) }}
                />
            )}
            {/* Reserve Switch badge shown in price area instead of corner */}
            <div className="p-4 relative z-10">
                {/* Mobile Layout (sm and below) - Match reference design */}
                <div className="block md:hidden relative pb-12">
                    {/* Top Row: Times, Airports, and Price */}
                    <div className="flex items-start justify-between mb-2">
                        <div className="flex items-start flex-1">
                            {/* Departure Time & Code */}
                            <div className="text-left">
                                <div className="text-base text-white">{departureTime}</div>
                                <div className="text-xs font-normal text-white/60">{firstSegment.origin.iata_code}</div>
                            </div>
                            
                            {/* Arrow Line - vertically centered with times */}
                            <div className="mx-4 flex items-center h-6">
                                <div className="text-white/50">⟶</div>
                            </div>
                            
                            {/* Arrival Time & Code */}
                            <div className="text-left">
                                <div className="text-base text-white">
                                    {arrivalTime}
                                    {arrivalDayOffset > 0 && <sup className="text-[9px] font-normal text-white/55 ml-px relative -top-[0.65em]">+{arrivalDayOffset}</sup>}
                                </div>
                                <div className="text-xs font-normal text-white/60">{lastSegment.destination.iata_code}</div>
                            </div>
                        </div>
                        
                        {/* Price - only show if not hidden */}
                        {!hidePrice && (
                            <div key={showAllInPrices ? 'allin' : 'fare'} className="text-right ml-6 transform-gpu">
                                {changeFlightDiff ? (
                                    <>
                                        <div className={`text-sm font-medium ${changeFlightDiff.cents === 0 ? 'text-white' : changeFlightDiff.isCheaper ? 'text-emerald-400' : 'text-amber-400'}`}>
                                            {changeFlightDiff.label}
                                        </div>
                                        {changeFlightDiff.sublabel ? <div className="text-xs font-normal text-white/60">{changeFlightDiff.sublabel}</div> : null}
                                    </>
                                ) : reserveSwitchBadge ? (
                                    <>
                                        <div className={`text-sm font-medium ${
                                            reserveSwitchBadge.type === 'cheaper' ? 'text-emerald-400' :
                                            reserveSwitchBadge.type === 'costs_more' ? 'text-amber-400' : 'text-white/80'
                                        }`}>
                                            {reserveSwitchBadge.label}
                                        </div>
                                        <div className="text-xs font-normal text-white/60">{reserveTotalLabel}</div>
                                    </>
                                ) : paymentMethod === 'reserve' ? (
                                    reserveExtra === 0 ? (
                                        <div className="text-sm font-medium text-emerald-400">$0+</div>
                                    ) : (
                                        <>
                                            <div className="text-sm font-medium text-white">+${Math.ceil(reserveExtra / 100).toLocaleString()}</div>
                                            <div className="text-xs font-normal text-white/60">{reserveTotalLabel}</div>
                                        </>
                                    )
                                ) : upfrontAmount && paymentMethod !== 'miles' ? (
                                    <>
                                        <div className="text-sm font-medium text-white">
                                            ${upfrontAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })} today
                                        </div>
                                        {paymentMethod === 'miles_installments' && milesOnlyDisplay ? (
                                            <div className="text-xs font-normal text-white/60">
                                                {milesOnlyDisplay}
                                            </div>
                                        ) : (
                                            <div className="text-xs font-normal text-white/60">
                                                {installmentsTotalLabel}
                                            </div>
                                        )}
                                    </>
                                ) : paymentMethod === 'defera_zero' ? (
                                    <>
                                        <div className="text-sm font-medium text-white">{formattedPrice}</div>
                                        <div className="text-xs font-normal text-white/60">
                                            {zeroTotalLabelDisplay}
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-base text-white">{formattedPrice}</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Bottom Row: Airline Logo and Details - Positioned at bottom left */}
                    <div className="absolute bottom-0 left-0 flex items-end gap-3">
                        {/* Airline Logo */}
                        <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0">
                            <img 
                                src={airlineLogo} 
                                alt={`${marketingAirline} logo`} 
                                className="w-8 h-8 object-contain"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = `<span class="text-white font-semibold text-sm">${marketingAirline.charAt(0)}</span>`;
                                }}
                            />
                        </div>
                        
                        {/* Flight Details */}
                        <div className="flex flex-col flex-1">
                            <div className="text-xs font-normal text-white mb-0.5 flex items-center gap-1">
                                <span>{stops === 0 ? 'Nonstop' : `${stops} stop${stops > 1 ? 's' : ''}`} · {totalDuration}</span>
                                {hasLongLayover && (
                                    <span title={longLayoverLabel} className="inline-flex" aria-label={longLayoverLabel}>
                                        <AlertTriangle className="w-3 h-3 text-amber-400" strokeWidth={2} />
                                    </span>
                                )}
                            </div>
                            <div className="text-xs font-normal text-white/60 flex items-center gap-1.5">
                                <span>{displayAirlineName}</span>
                                {currentFlightTag}
                            </div>
                            {statusInfo && (
                              <div className="mt-1">
                                <Badge className={`text-xs h-5 px-1.5 py-0 ${statusInfo.className}`}>
                                  {statusInfo.label}
                                </Badge>
                              </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="absolute bottom-0 right-0 flex items-center gap-3 pl-12">
                        {!hideBaggageIcons && <BaggageAllowanceIcons allowance={baggageAllowance} />}
                        {isSelectable && (isExpanded ? <ChevronUp className="w-4 h-4 text-white/60" /> : <ChevronDown className="w-4 h-4 text-white/60" />)}
                    </div>
                </div>

                {/* Desktop Layout (md and up) - Defera Flights style grid */}
                <div className="hidden md:block">
                    <div className={`grid items-center gap-x-4 ${desktopGridCols}`}>
                        {/* Column 1: Logo */}
                        <div className="col-span-1 flex items-center justify-start">
                            <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0">
                                <img 
                                    src={airlineLogo} 
                                    alt={`${marketingAirline} logo`} 
                                    className="w-9 h-9 object-contain"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = `<span class="text-white font-semibold text-sm">${marketingAirline.charAt(0)}</span>`;
                                    }}
                                />
                            </div>
                        </div>

                        {/* Column 2: Times & Airline Name */}
                        <div className="col-span-3">
                            <div className="text-white">
                                {departureTime} – {arrivalTime}
                                {arrivalDayOffset > 0 && <sup className="text-[9px] font-normal text-white/55 ml-px relative -top-[0.65em]">+{arrivalDayOffset}</sup>}
                            </div>
                            <div className="text-xs font-normal text-white/60 flex items-center gap-1.5 min-w-0">
                                <span className="truncate">{displayAirlineName}</span>
                                {currentFlightTag}
                            </div>
                        </div>

                        {/* Column 3: Duration & Route */}
                        <div className="col-span-3">
                             <div className="text-sm text-white">{totalDuration}</div>
                             <div className="text-xs font-normal text-white/60">{firstSegment.origin.iata_code}–{lastSegment.destination.iata_code}</div>
                        </div>

                        {/* Column 4: Stops */}
                        <div className="col-span-2">
                            <div className="text-sm text-white flex items-center gap-1">
                                <span>{stops === 0 ? 'Nonstop' : `${stops} stop${stops > 1 ? 's' : ''}`}</span>
                                {hasLongLayover && (
                                    <span title={longLayoverLabel} className="inline-flex" aria-label={longLayoverLabel}>
                                        <AlertTriangle className="w-3.5 h-3.5 text-amber-400" strokeWidth={2} />
                                    </span>
                                )}
                            </div>
                            <div className={`text-xs font-normal ${hasLongLayover ? 'text-amber-400/90' : 'text-white/60'}`}>
                                {stops === 1
                                    ? `${layoverDuration ? `${layoverDuration} ` : ''}in ${slice.segments[0].destination.iata_code}`
                                    : stops > 1
                                        ? (hasLongLayover && longestLayoverAirport
                                            ? `${parseAndFormatDuration(longestLayoverMins)} in ${longestLayoverAirport}`
                                            : 'Multiple')
                                        : ''}
                            </div>
                        </div>
                        
                        {/* Column 5: Price OR Status + baggage */}
                        <div className="col-span-3 flex justify-end items-center gap-x-4">
                            {!hideBaggageIcons && (
                              <BaggageAllowanceIcons
                                allowance={baggageAllowance}
                                iconClassName="w-3.5 h-3.5"
                                className="flex items-center gap-2.5 text-sm font-normal text-white/60"
                              />
                            )}
                            {!hidePrice ? (
                                <div key={showAllInPrices ? 'allin' : 'fare'} className="text-right transform-gpu">
                                    {changeFlightDiff ? (
                                        <>
                                            <div className={`text-base font-medium ${changeFlightDiff.cents === 0 ? 'text-white' : changeFlightDiff.isCheaper ? 'text-emerald-400' : 'text-amber-400'}`}>
                                                {changeFlightDiff.label}
                                            </div>
                                            {changeFlightDiff.sublabel ? <div className="text-xs font-normal text-white/60">{changeFlightDiff.sublabel}</div> : null}
                                        </>
                                    ) : reserveSwitchBadge ? (
                                        <>
                                            <div className={`text-base font-medium ${
                                                reserveSwitchBadge.type === 'cheaper' ? 'text-emerald-400' :
                                                reserveSwitchBadge.type === 'costs_more' ? 'text-amber-400' : 'text-white/80'
                                            }`}>
                                                {reserveSwitchBadge.label}
                                            </div>
                                            <div className="text-xs font-normal text-white/60">{reserveTotalLabel}</div>
                                        </>
                                    ) : paymentMethod === 'reserve' ? (
                                        reserveExtra === 0 ? (
                                            <div className="text-base font-medium text-emerald-400">$0+</div>
                                        ) : (
                                            <>
                                                <div className="text-base font-medium text-white">+${Math.ceil(reserveExtra / 100).toLocaleString()}</div>
                                                <div className="text-xs font-normal text-white/60">{reserveTotalLabel}</div>
                                            </>
                                        )
                                    ) : upfrontAmount && paymentMethod !== 'miles' ? (
                                        <>
                                            <div className="text-base font-medium text-white">
                                                ${upfrontAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })} today
                                            </div>
                                            {paymentMethod === 'miles_installments' && milesOnlyDisplay ? (
                                                <div className="text-xs font-normal text-white/60">
                                                    {milesOnlyDisplay}
                                                </div>
                                            ) : (
                                                <div className="text-xs font-normal text-white/60">
                                                    {installmentsTotalLabel}
                                                </div>
                                            )}
                                        </>
                                    ) : paymentMethod === 'defera_zero' ? (
                                        <>
                                            <div className="text-base font-medium text-white">{formattedPrice}</div>
                                            <div className="text-xs font-normal text-white/60">
                                                {zeroTotalLabelDisplay}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-lg text-white">{formattedPrice}</div>
                                    )}
                                </div>
                            ) : statusInfo ? (
                                <Badge className={`text-xs ${statusInfo.className}`}>
                                    {statusInfo.label}
                                </Badge>
                            ) : null}

                            {isSelectable && 
                                <div className="text-white/60">
                                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>

            {/* Expandable Detail Section */}
            {isExpanded && (
                <div className="border-t border-white/10 bg-gradient-to-b from-black to-amber-950"
                >
                        <div className="p-4 space-y-4">
                            {/* Flight Segments */}
                            {slice.segments.map((segment, index) => (
                                <div key={index} className="space-y-3">
                                    {/* Segment Header */}
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded flex items-center justify-center">
                                            <img
                                                src={resolveCarrierLogo(segment.marketing_carrier) || airlineLogo}
                                                alt={`${segment.marketing_carrier?.name || marketingAirline} logo`}
                                                className="w-5 h-5 object-contain"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    const name = segment.marketing_carrier?.name || marketingAirline;
                                                    e.target.parentElement.innerHTML = `<span class="text-white font-semibold text-sm">${name.charAt(0)}</span>`;
                                                }}
                                            />
                                        </div>
                                        <div className="text-xs md:text-sm font-normal text-white">
                                            {cleanAirlineName(segment.marketing_carrier?.name || marketingAirline)} • {segment.marketing_carrier_flight_number || 'Flight'}
                                        </div>
                                        {/* Inventory-source label — right-aligned on the airline + flight-
                                            number row, first segment only (skip on connecting segments so
                                            we don't repeat the same channel tag per leg). Purely
                                            informational for power users. */}
                                        {index === 0 && onFlightSelect && (() => {
                                            const sourceLabel = getInventorySourceLabel(offer);
                                            if (!sourceLabel) return null;
                                            return (
                                                <span
                                                    className="ml-auto text-[9px] font-normal tracking-[0.08em] uppercase text-white/15"
                                                    title="Fare-distribution channel"
                                                >
                                                    {sourceLabel}
                                                </span>
                                            );
                                        })()}
                                    </div>

                                    {/* Flight Timeline */}
                                    <div className="flex items-start gap-4 ml-2">
                                        <div className="flex flex-col items-center">
                                            <CircleDot className="w-3 h-3 text-white/60 flex-shrink-0" />
                                            <div className="w-px h-8 bg-white/20 my-1.5"></div>
                                            <CircleDot className="w-3 h-3 text-white/60 flex-shrink-0" />
                                        </div>
                                        <div className="flex-1 space-y-2">
                                            {/* Departure */}
                                            <div>
                                                <div className="text-xs md:text-sm font-normal text-white">
                                                    {formatTime12Hour(segment.departing_at)} • {getAirportName(segment.origin)} ({segment.origin.iata_code})
                                                </div>
                                            </div>

                                            {/* Flight Time - In Between */}
                                            {segment.duration && (
                                                <div className="text-[10px] md:text-xs font-normal text-white/60">
                                                    {parseAndFormatDuration(segment.duration)}
                                                </div>
                                            )}

                                            {/* Arrival */}
                                            <div>
                                                <div className="text-xs md:text-sm font-normal text-white">
                                                    {formatTime12Hour(segment.arriving_at)} • {getAirportName(segment.destination)} ({segment.destination.iata_code})
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Aircraft & Amenities */}
                                    <div className="ml-9 pl-1 space-y-1.5 mt-1">
                                        {segment.aircraft && (
                                            <div className="text-[10px] md:text-xs font-normal text-white/60">
                                                {segment.aircraft.name}
                                                {segment.operating_carrier?.name && segment.operating_carrier.name !== segment.marketing_carrier?.name && (
                                                    <span> • Operated by {cleanAirlineName(segment.operating_carrier.name)}</span>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Amenities */}
                                        <div className="flex items-center gap-4 text-[10px] md:text-xs font-normal text-white/60">
                                            <div className="flex items-center gap-1">
                                                <Wifi className="w-3 h-3" />
                                                <span>Wi-Fi</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Zap className="w-3 h-3" />
                                                <span>Power</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Monitor className="w-3 h-3" />
                                                <span>Entertainment</span>
                                            </div>
                                        </div>
                                        
                                        {/* Cabin Class and Seat Assignments (for booked flights only) */}
                                        {(() => {
                                            const cabinClass = segment.passengers_cabin_class_marketing_name || 
                                                             segment.cabin_class_marketing_name || 
                                                             segment.cabin_class;
                                            const seatData = segmentSeatAssignments?.[segment.id];
                                            
                                            if (cabinClass || seatData) {
                                                return (
                                                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] md:text-xs font-medium text-white/80">
                                                        {cabinClass && <span>{cabinClass}</span>}
                                                        <SegmentSeatChips seats={seatData} className="text-[10px] md:text-xs" />
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>


                                    {/* Layover Info */}
                                    {index < slice.segments.length - 1 && (
                                        <div className="flex items-start gap-4 ml-2">
                                            <div className="flex flex-col items-center">
                                                <CircleDot className="w-3 h-3 text-amber-400" />
                                            </div>
                                            <div className="text-[10px] md:text-xs font-normal text-amber-400 -mt-1 p-2 bg-white/5 rounded">
                                                Layover: {parseAndFormatDuration(slice.segments[index + 1].layover_duration)} in {segment.destination.iata_code}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Select Flight Button — Conditionally Rendered.
                                (Inventory-source label lives on the segment header row above,
                                first segment only — see `getInventorySourceLabel` render.) */}
                            {onFlightSelect && (
                                <div className="pt-4 border-t border-white/10">
                                    <Button
                                        disabled={changeFlightCurrent && !changeFlightKeepable}
                                        className="group/select w-full sm:w-auto sm:min-w-[170px] h-9 rounded-full px-5 text-xs font-medium tracking-wide text-white transition-all bg-[var(--accent-color)] hover:bg-[hsl(220,75%,30%)] shadow-[0_0_16px_hsl(220,75%,35%,0.35)] hover:shadow-[0_0_20px_hsl(220,75%,35%,0.45)] disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                                        onClick={handleSelectFlight}
                                    >
                                        <span className="flex items-center justify-center gap-1.5">
                                            {changeFlightCurrent
                                                ? (changeFlightKeepable ? 'Keep this flight' : 'Current flight')
                                                : (isReturnJourney ? 'Select return flight' : 'Select flight')}
                                            {(!changeFlightCurrent || changeFlightKeepable) && (
                                                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/select:translate-x-0.5" />
                                            )}
                                        </span>
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )
            }
        </div>
    );
}

export default memo(FlightOfferCard);
